import { RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { AirportListItemType } from '@lp/features/RoutePlanner/types';
import { airportsMapper } from '@lp/features/RoutePlanner/components/RouteLegForm/utils';
import { getAirportData } from '@lp/features/RoutePlanner/components/RouteLegForm/context/AirportLocationContext';
import { AirportsQueryFilters, useInfiniteAirportsListQueryByCode } from '@/queries/airports';
import { useInfiniteDropdown } from '@/hooks/useInfiniteDropdown';

export type UseAirportsType = {
  airportsList: AirportListItemType[];
  airportsScrollerRef: RefObject<HTMLElement>;
  selectedAirport: AirportListItemType | undefined;
  isAirportsListLoading: boolean;
  searchValue: string;
  onAirportSearch: (text: string) => void;
  onAirportSelectChange: (key: string) => Promise<AirportListItemType | null>;
  setAirportsAutocompleteOpen: (value: boolean) => void;
  getSelectedAirport: (id: string) => Promise<AirportListItemType | null>;
};

type UseAirportsProps = {
  selectedId?: string;
  selectedValue?: string;
  filters?: Partial<AirportsQueryFilters>;
  setValue: (value: string) => void;
};

export const useAirports = ({ selectedId, selectedValue, filters, setValue }: UseAirportsProps): UseAirportsType => {
  const [selectedAirport, setSelectedAirport] = useState<AirportListItemType | undefined>(undefined);

  const {
    list: airportsList,
    isLoading: isAirportsListLoading,
    setSearchValue: onAirportSearch,
    setAutocompleteOpen: setAirportsAutocompleteOpen,
    itemsScrollerRef: airportsScrollerRef,
    searchValue,
    onItemSelectChange,
  } = useInfiniteDropdown({
    setValue,
    listQuery: useInfiniteAirportsListQueryByCode,
    mapper: airportsMapper,
    initialName: selectedValue,
    clearOnSearch: false,
    defaultFilters: filters,
  });

  const dropdownList = useMemo(() => {
    if (selectedAirport?.value !== selectedId) {
      return airportsList;
    }

    return [];
  }, [airportsList, selectedAirport, selectedId]);

  const getSelectedAirport = useCallback(
    async (id: string): Promise<AirportListItemType | null> => {
      const airport = dropdownList.find((item) => item.value === id);

      if (!airport) {
        return getAirportData(id);
      }

      return airport;
    },
    [dropdownList],
  );

  useEffect(() => {
    if (selectedId && selectedAirport?.value !== selectedId) {
      getSelectedAirport(selectedId).then((airport) => {
        if (airport) {
          setSelectedAirport(airport);
          onItemSelectChange(airport.value);
        }
      });
    }
  }, [selectedAirport, selectedId, getSelectedAirport, onItemSelectChange]);

  const list = useMemo(() => {
    if (!selectedAirport) {
      return airportsList;
    }

    const filteredAirports = airportsList.filter((a) => a.value !== selectedAirport.value);

    return [selectedAirport, ...filteredAirports];
  }, [airportsList, selectedAirport]);

  const onAirportSelectChange = useCallback(
    async (key: string) => {
      if (key === 'clear') {
        setSelectedAirport(undefined);
        onItemSelectChange('');
        return null;
      }

      onItemSelectChange(key);

      const selectedItem = await getSelectedAirport(key);

      if (selectedItem) {
        setSelectedAirport(selectedItem);
      }

      return selectedItem;
    },
    [getSelectedAirport, onItemSelectChange],
  );

  return {
    airportsList: list,
    isAirportsListLoading,
    onAirportSearch,
    setAirportsAutocompleteOpen,
    airportsScrollerRef,
    searchValue,
    selectedAirport,
    onAirportSelectChange,
    getSelectedAirport,
  };
};
